import type * as React from 'react'
import { useCallback, useState, useEffect } from 'react'
import { Temporal } from '@js-temporal/polyfill'
import { clsx } from 'clsx'

import { Calendar } from '../Calendar'
import { TimeInput, type TimeSuggestionsProp } from '../TimeInput'
import { Separator } from '../Separator'

import styles from './DateTimePicker.module.css'

/**
 * Extracts a Temporal.PlainDate from a Temporal.ZonedDateTime.
 *
 * @param zonedDateTime - The ZonedDateTime to extract the date from
 * @returns A PlainDate representing just the date portion, or undefined if no input provided
 * @example
 * const date = zonedDateTimeToPlainDate(myZonedDateTime) // Returns PlainDate
 * const empty = zonedDateTimeToPlainDate(undefined) // Returns undefined
 */
function zonedDateTimeToPlainDate(
  zonedDateTime?: Temporal.ZonedDateTime,
): Temporal.PlainDate | undefined {
  if (!zonedDateTime) return undefined
  return zonedDateTime.toPlainDate()
}

/**
 * Extracts a Temporal.PlainTime from a Temporal.ZonedDateTime.
 *
 * @param zonedDateTime - The ZonedDateTime to extract the time from
 * @returns A PlainTime representing just the time portion, or undefined if no input provided
 * @example
 * const time = zonedDateTimeToPlainTime(myZonedDateTime) // Returns PlainTime
 * const empty = zonedDateTimeToPlainTime(undefined) // Returns undefined
 */
function zonedDateTimeToPlainTime(
  zonedDateTime?: Temporal.ZonedDateTime,
): Temporal.PlainTime | undefined {
  if (!zonedDateTime) return undefined
  return zonedDateTime.toPlainTime()
}

/**
 * Creates a Temporal.ZonedDateTime by combining a date, time and timezone.
 *
 * @param date - The PlainDate to use for the date portion
 * @param time - The PlainTime to use for the time portion
 * @param timezone - The timezone to create the ZonedDateTime in
 * @returns A new ZonedDateTime combining the date and time in the specified timezone
 * @example
 * const zdt = createZonedDateTime(
 *   Temporal.PlainDate.from('2024-01-01'),
 *   Temporal.PlainTime.from('09:00'),
 *   'America/New_York'
 * )
 */
function createZonedDateTime(
  date: Temporal.PlainDate,
  time: Temporal.PlainTime,
  timezone: Temporal.TimeZoneLike,
): Temporal.ZonedDateTime {
  // Combine the date and time into a PlainDateTime
  const dateTime = date.toPlainDateTime(time)
  // Convert to ZonedDateTime using the specified timezone
  return dateTime.toZonedDateTime(timezone)
}

export interface DateTimePickerProps
  extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'defaultValue' | 'value' | 'onChange'
  > {
  /**
   * The current value (controlled)
   */
  value?: Temporal.ZonedDateTime
  /**
   * The default value (uncontrolled)
   */
  defaultValue?: Temporal.ZonedDateTime
  /**
   * Handler that is called when the value changes
   */
  onValueChange?: (value: Temporal.ZonedDateTime | undefined) => void
  /**
   * Handler that is called when the time changes
   */
  onTimeChange?: (value: Temporal.PlainTime | null) => void
  /**
   * Handler that is called when the date changes
   */
  onDateChange?: (value: Temporal.PlainDate | null) => void
  /**
   * The timezone to use for the date time picker
   * @default Local timezone from browser
   */
  timezone?: string
  /**
   * The minimum allowed date and time.
   * If the selected date is the same as the date part of `min`,
   * the time part of `min` will be used as the `minTime` for that day,
   * unless a separate `minTime` prop is also provided (which would take precedence).
   */
  min?: Temporal.ZonedDateTime
  /**
   * The maximum allowed date and time.
   * If the selected date is the same as the date part of `max`,
   * the time part of `max` will be used as the `maxTime` for that day,
   * unless a separate `maxTime` prop is also provided (which would take precedence).
   */
  max?: Temporal.ZonedDateTime
  /**
   * The minimum allowed time (without date restrictions)
   * e.g. 9:00 AM for business hours
   */
  minTime?: Temporal.PlainTime
  /**
   * The maximum allowed time (without date restrictions)
   * e.g. 5:00 PM for business hours
   */
  maxTime?: Temporal.PlainTime
  /**
   * Whether to use 12 or 24 hour format
   * @default 12
   */
  hourCycle?: 12 | 24
  /**
   * The first day of the week in the calendar
   * 0 = Sunday, 1 = Monday
   * @default 1 (Monday)
   */
  weekStartsOn?: 0 | 1
  /**
   * Additional CSS class for the component
   */
  className?: string
  /**
   * Time suggestions configuration object
   */
  timeSuggestions?: TimeSuggestionsProp
  /**
   * Required
   */
  required?: boolean
}

/**
 * DateTimePicker component that allows users to select both a date and time.
 * Returns a Temporal.ZonedDateTime object with timezone information.
 */
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  defaultValue,
  onValueChange,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  min,
  max,
  minTime,
  maxTime,
  hourCycle = 12,
  weekStartsOn = 1,
  className,
  timeSuggestions,
  onTimeChange,
  onDateChange,
  required = false,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState<
    Temporal.ZonedDateTime | undefined
  >(value !== undefined ? value : defaultValue)

  useEffect(
    function updateInternalValue() {
      if (value !== undefined) {
        setInternalValue(value)
      }
    },
    [value],
  )

  const effectiveValue = value !== undefined ? value : internalValue

  const dateValue = zonedDateTimeToPlainDate(effectiveValue)
  const timeValue = zonedDateTimeToPlainTime(effectiveValue)

  const handleDateChange = useCallback(
    (date?: Temporal.PlainDate) => {
      if (!date) {
        // If date is cleared, clear the whole value
        if (onValueChange) {
          onValueChange(undefined)
        } else {
          setInternalValue(undefined)
        }
        return
      }

      const time = timeValue || new Temporal.PlainTime()
      const newValue = createZonedDateTime(date, time, timezone)

      onDateChange?.(date)
      if (onValueChange) {
        onValueChange(newValue)
      } else {
        setInternalValue(newValue)
      }
    },
    [onValueChange, timeValue, timezone, onDateChange],
  )

  const handleTimeChange = useCallback(
    (time: Temporal.PlainTime | null) => {
      if (!time || !dateValue) {
        // Can't have a time without a date
        return
      }

      const newValue = createZonedDateTime(dateValue, time, timezone)

      onTimeChange?.(time)
      if (onValueChange) {
        onValueChange(newValue)
      } else {
        setInternalValue(newValue)
      }
    },
    [onValueChange, dateValue, timezone, onTimeChange],
  )

  const minDate = min ? min.toPlainDate() : undefined
  const maxDate = max ? max.toPlainDate() : undefined

  let effectiveMinTime: Temporal.PlainTime | undefined = minTime

  if (!effectiveMinTime && min && dateValue) {
    const overallMinDate = min.toPlainDate()
    if (dateValue.equals(overallMinDate)) {
      effectiveMinTime = min.toPlainTime()
    }
  }

  let effectiveMaxTime: Temporal.PlainTime | undefined = maxTime

  if (!effectiveMaxTime && max && dateValue) {
    const overallMaxDate = max.toPlainDate()
    if (dateValue.equals(overallMaxDate)) {
      effectiveMaxTime = max.toPlainTime()
    }
  }

  return (
    <div className={clsx(styles.root, className)} {...rest}>
      <Calendar
        value={dateValue}
        onValueChange={handleDateChange}
        timezone={timezone}
        weekStartsOn={weekStartsOn}
        min={minDate}
        max={maxDate}
        required={required}
      />
      <Separator className={styles.separator} />
      <TimeInput
        aria-label="Time selector"
        value={timeValue}
        onChange={handleTimeChange}
        disabled={!dateValue}
        min={effectiveMinTime}
        max={effectiveMaxTime}
        hourCycle={hourCycle}
        timeSuggestions={timeSuggestions}
        timezone={timezone}
      />
    </div>
  )
}
